/**
 * `/api/me` — the single source of truth for who the user is and what tier
 * they hold.
 *
 * Phase 1 step 7: drive UI role checks from THIS, not from a client-side token
 * decode. The server's answer is authoritative, and it resolves the tier with
 * the same `resolveTier` from `@menlo/shared` that the server enforces with —
 * so a button the UI shows is a button the server will accept.
 *
 * The UI hiding is convenience only; §2 rule 1 is that authorization is
 * enforced server-side on every request.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  canChangeOpportunityStatus,
  canSetOpportunityFlags,
  canWriteOpportunity,
  type Me,
  type OpportunityOwnership,
} from '@menlo/shared';
import { api, ApiError } from '@/lib/api';

export const ME_QUERY_KEY = ['me'] as const;

export function useMe(): UseQueryResult<Me, ApiError> {
  return useQuery<Me, ApiError>({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api<Me>('/api/me'),
    // Identity does not change under the user mid-session; a role change in
    // Entra is not immediate anyway — it waits for the next token, 68 minutes
    // at most (§8c [R6]).
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Never retry a decision. 401/403 are answers, not failures, and
      // retrying them turns "access not provisioned" into a spinner.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
      return failureCount < 2;
    },
  });
}

/**
 * The client-side mirror of §2's one scope predicate. Calls the same shared
 * function the server does — that is the whole reason it lives in
 * `packages/shared`.
 */
export function useCanWriteOpportunity(): (opp: OpportunityOwnership) => boolean {
  const { data } = useMe();
  return (opp) => {
    if (!data) return false;
    return canWriteOpportunity({ userId: data.user.id, tier: data.tier }, opp);
  };
}

/**
 * The client-side mirror of `canChangeOpportunityStatus` (decision 12): may
 * this user move this deal to `toStatus`? It is what hides the Invalid outcome,
 * and Reopen on an Invalid deal, from a User — the same function the API
 * refuses with.
 */
export function useCanChangeOpportunityStatus(): (
  opp: OpportunityOwnership & { status: string },
  toStatus: string,
) => boolean {
  const { data } = useMe();
  return (opp, toStatus) => {
    if (!data) return false;
    return canChangeOpportunityStatus({ userId: data.user.id, tier: data.tier }, opp, toStatus);
  };
}

/**
 * The client-side mirror of `canSetOpportunityFlags` (session 4): may this user
 * tick Focus or Verified? A User sees the boxes, disabled — the same function
 * the API refuses with.
 */
export function useCanSetOpportunityFlags(): boolean {
  const { data } = useMe();
  if (!data) return false;
  return canSetOpportunityFlags({ userId: data.user.id, tier: data.tier });
}
